import { useCallback, useRef } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { playStreamedMp3Audio } from '../utils/streamAudioPlayer';
import { logMessage } from '../utils/logMessage';

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

type AzureSpeechSynthesisArgs = {
    isMuted: boolean;
    volume: number;
    setBlockMic: (b: boolean) => void;
    setStatusText?: (msg: string) => void;
};

type SpeakOptions = {
    text: string;
    token: string;
    region: string;
    voiceName?: string;
    onEnd?: () => void;
    onError?: (e: any) => void;
    recognitionTimestamp?: number;
    maxRetries?: number;
    initialDelay?: number;
    setBadState: (b: boolean) => void;
};

type SpeechCredentials = { token: string; region: string };

type InternalSpeakOptions = SpeakOptions & {
    fetchSpeechToken?: () => Promise<SpeechCredentials>;
    setSpeechCredentials?: (creds: SpeechCredentials) => void;
};

const toError = (value: unknown): Error => {
    if (value instanceof Error) {
        return value;
    }
    if (typeof value === 'string') {
        return new Error(value);
    }
    return new Error('Unknown synthesis error');
};

const speakTextAsyncPromise = (
    synthesizer: SpeechSDK.SpeechSynthesizer,
    text: string
) => new Promise<void>((resolve, reject) => {
    synthesizer.speakTextAsync(
        text,
        () => {
            synthesizer.close();
            resolve();
        },
        (error) => {
            synthesizer.close();
            reject(toError(error));
        }
    );
});

async function runSynthesisAttempt(
    options: {
        text: string;
        voiceName: string;
        recognitionTimestamp?: number;
        onEnd?: () => void;
        onError?: (e: any) => void;
    },
    credentials: SpeechCredentials,
    isCurrentRequest: () => boolean,
    playback: {
        isMuted: boolean;
        volume: number;
        setBlockMic: (b: boolean) => void;
    }
) {
    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(credentials.token, credentials.region);
    speechConfig.speechSynthesisVoiceName = options.voiceName;
    speechConfig.speechSynthesisOutputFormat = SpeechSDK.SpeechSynthesisOutputFormat.Audio16Khz32KBitRateMonoMp3;

    const stream = SpeechSDK.AudioOutputStream.createPullStream();
    const audioConfig = SpeechSDK.AudioConfig.fromStreamOutput(stream);
    const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, audioConfig);

    synthesizer.SynthesisCanceled = (_s, e) => {
        if (!isCurrentRequest()) {
            synthesizer.close();
            return;
        }
        const details = SpeechSDK.CancellationDetails.fromResult(e.result);
        if (options.onError) options.onError(details);
        logMessage('[TTS] SynthesisCanceled event', 'error', { details: JSON.stringify(details) });
        synthesizer.close();
    };

    const audioPromise = playStreamedMp3Audio(
        stream,
        playback.isMuted,
        playback.volume,
        () => {
            if (!isCurrentRequest()) {
                return;
            }
            playback.setBlockMic(false);
            if (options.onEnd) options.onEnd();
        },
        options.recognitionTimestamp
    );

    try {
        await Promise.all([audioPromise, speakTextAsyncPromise(synthesizer, options.text)]);
    } catch (error) {
        if (isCurrentRequest()) {
            playback.setBlockMic(false);
            logMessage('[TTS] Synthesis error in speakTextAsync', 'error', { error: JSON.stringify(error) });
        }
        throw error;
    }
}

async function refreshSpeechCredentials(
    attemptCount: number,
    fetchSpeechToken: (() => Promise<SpeechCredentials>) | undefined,
    setSpeechCredentials: ((creds: SpeechCredentials) => void) | undefined,
    isCurrentRequest: () => boolean
): Promise<SpeechCredentials | null> {
    if (attemptCount < 2 || !fetchSpeechToken) {
        return null;
    }
    const fresh = await fetchSpeechToken();
    if (!isCurrentRequest()) {
        return null;
    }
    if (setSpeechCredentials) {
        setSpeechCredentials(fresh);
    }
    return fresh;
}

function handleFinalFailure(
    error: unknown,
    attemptCount: number,
    isCurrentRequest: () => boolean,
    setBlockMic: (b: boolean) => void,
    setStatusText: ((msg: string) => void) | undefined,
    setBadState: (b: boolean) => void,
    onError: ((e: any) => void) | undefined
) {
    if (!isCurrentRequest()) {
        return;
    }
    setBlockMic(false);
    if (setStatusText) {
        if ((error as any)?.target instanceof HTMLAudioElement) {
            setStatusText('AN ERROR HAS OCCURRED! CLICK SOS ABOVE TO CONTINUE THE SESSION.');
        } else {
            setStatusText(`TTS failed after ${attemptCount} attempts.`);
        }
    }
    logMessage('[TTS] Max retries exceeded, giving up.', 'error', { error: JSON.stringify(error) });
    if (onError) onError(error);
    setBadState(true);
}

type NumberRef = { current: number };

async function processAttemptFailure(
    error: unknown,
    attempt: number,
    maxRetries: number,
    currentCredentials: SpeechCredentials,
    retryDelay: number,
    deps: {
        isCurrentRequest: () => boolean;
        fetchSpeechToken?: () => Promise<SpeechCredentials>;
        setSpeechCredentials?: (creds: SpeechCredentials) => void;
        setStatusText?: (msg: string) => void;
        setBadState: (b: boolean) => void;
        onError?: (e: any) => void;
        setBlockMic: (b: boolean) => void;
    }
): Promise<{ shouldStop: boolean; nextCredentials: SpeechCredentials; nextDelay: number }> {
    if (!deps.isCurrentRequest()) {
        return { shouldStop: true, nextCredentials: currentCredentials, nextDelay: retryDelay };
    }

    const attemptCount = attempt + 1;
    logMessage(
        `[TTS] Error in synthesis/playback attempt #${attemptCount}`,
        'error',
        { error: JSON.stringify(error) }
    );

    let nextCredentials = currentCredentials;
    try {
        const fresh = await refreshSpeechCredentials(
            attemptCount,
            deps.fetchSpeechToken,
            deps.setSpeechCredentials,
            deps.isCurrentRequest
        );
        if (fresh) {
            nextCredentials = fresh;
        }
    } catch (tokenErr) {
        if (!deps.isCurrentRequest()) {
            return { shouldStop: true, nextCredentials, nextDelay: retryDelay };
        }
        if (deps.setStatusText) {
            deps.setStatusText('Failed to refresh Azure Speech token.');
        }
        deps.setBadState(true);
        if (deps.onError) deps.onError(tokenErr);
        return { shouldStop: true, nextCredentials, nextDelay: retryDelay };
    }

    if (attempt >= maxRetries) {
        handleFinalFailure(
            error,
            attemptCount,
            deps.isCurrentRequest,
            deps.setBlockMic,
            deps.setStatusText,
            deps.setBadState,
            deps.onError
        );
        return { shouldStop: true, nextCredentials, nextDelay: retryDelay };
    }

    await wait(retryDelay);
    if (!deps.isCurrentRequest()) {
        return { shouldStop: true, nextCredentials, nextDelay: retryDelay };
    }
    return { shouldStop: false, nextCredentials, nextDelay: retryDelay * 2 };
}

async function executeSynthesisWithRetries(
    requestId: number,
    activeRequestIdRef: NumberRef,
    options: InternalSpeakOptions,
    playback: {
        isMuted: boolean;
        volume: number;
        setBlockMic: (b: boolean) => void;
        setStatusText?: (msg: string) => void;
    }
) {
    const {
        text,
        token,
        region,
        voiceName = 'en-US-AvaMultilingualNeural',
        onEnd,
        onError,
        recognitionTimestamp,
        maxRetries = 3,
        initialDelay = 500,
        setBadState,
        fetchSpeechToken,
        setSpeechCredentials
    } = options;

    let currentCredentials: SpeechCredentials = { token, region };
    let retryDelay = initialDelay;

    const isCurrentRequest = () => activeRequestIdRef.current === requestId;

    playback.setBlockMic(true);

    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        if (!isCurrentRequest()) {
            return;
        }
        try {
            await runSynthesisAttempt(
                {
                    text,
                    voiceName,
                    recognitionTimestamp,
                    onEnd,
                    onError,
                },
                currentCredentials,
                isCurrentRequest,
                { isMuted: playback.isMuted, volume: playback.volume, setBlockMic: playback.setBlockMic }
            );
            return;
        } catch (e: unknown) {
            const outcome = await processAttemptFailure(
                e,
                attempt,
                maxRetries,
                currentCredentials,
                retryDelay,
                {
                    isCurrentRequest,
                    fetchSpeechToken,
                    setSpeechCredentials,
                    setStatusText: playback.setStatusText,
                    setBadState,
                    onError,
                    setBlockMic: playback.setBlockMic,
                }
            );

            if (outcome.shouldStop) {
                return;
            }
            currentCredentials = outcome.nextCredentials;
            retryDelay = outcome.nextDelay;
        }
    }
}

export function useAzureSpeechSynthesis({ 
    isMuted, 
    volume, 
    setBlockMic, 
    setStatusText 
}: AzureSpeechSynthesisArgs) {
    const activeRequestIdRef = useRef(0);

    return useCallback((options: InternalSpeakOptions) => {
        const requestId = activeRequestIdRef.current + 1;
        activeRequestIdRef.current = requestId;

        return executeSynthesisWithRetries(requestId, activeRequestIdRef, options, {
            isMuted,
            volume,
            setBlockMic,
            setStatusText,
        });
    }, [isMuted, volume, setBlockMic, setStatusText]);
}